import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ExternalLink, Star } from 'lucide-react';
import SEO from '../components/SEO';
import GlobalSearch from '../components/GlobalSearch';
import ThemeSidebar from '../components/sidebars/ThemeSidebar';
import themesData from '../data/themes.json';
import { Button } from '../components/ui/button';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import AdSlot from '../components/AdSlot';

const Templates: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const searchQuery = searchParams.get('search') || '';

  const filteredThemes = useMemo(() => {
    let themes = [...themesData];

    // Filter by category
    if (selectedCategory !== 'all') {
      themes = themes.filter((theme: any) => theme.category === selectedCategory);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      themes = themes.filter((theme: any) =>
        theme.title.toLowerCase().includes(query) ||
        theme.tags?.some((tag: string) => tag.toLowerCase().includes(query)) ||
        theme.category.toLowerCase().includes(query)
      );
    }

    return themes;
  }, [selectedCategory, searchQuery]);

  const categories = [
    { label: 'All Themes', value: 'all' },
    { label: 'Blogger', value: 'blogger' },
    { label: 'WordPress', value: 'wordpress' },
  ];

  return (
    <>
      <SEO
        title="Premium Website Templates"
        description="Browse our collection of professional Blogger and WordPress themes. High-quality, responsive templates for every business."
      />
      <BreadcrumbSchema
  items={[
    { name: "Home", url: "https://www.thecodebazaar.com/" },
    { name: "Templates", url: "https://www.thecodebazaar.com/templates" },
  ]}
/>


      <div className="bg-gradient-to-br from-sky-50 to-blue-50 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center">
            Premium Website Templates
          </h1>
          <p className="text-lg text-gray-600 text-center mb-8">
            Professional themes for Blogger and WordPress
          </p>

          {/* Large Search Box */}
          <div className="max-w-2xl mx-auto">
            <GlobalSearch size="large" placeholder="Search themes by name, category, or tags..." />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col md:grid lg:grid-cols-4 gap-8">
          

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 mb-8">
              {categories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                    selectedCategory === category.value
                      ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-sky-500 hover:text-sky-500'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>

            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredThemes.length} {filteredThemes.length === 1 ? 'theme' : 'themes'}
                {searchQuery && ` for "${searchQuery}"`}
              </p>
            </div>

            {/* Themes Grid */}
            {filteredThemes.length > 0 ? (
              <div className="flex flex-col md:grid md:grid-cols-2 gap-6">
                {/* {filteredThemes.map((theme: any) => ( */}
                  {[...filteredThemes].reverse().map((theme: any) => (
                  <div
                    key={theme.id}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="relative overflow-hidden aspect-[16/9]">
                      {theme.type === 'external' ? (
                          <a
                            href={theme.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block"
                          >
                      <img
                        src={theme.thumbnail || `https://placehold.co/600x400/dee3f8/303b5c?text=${encodeURIComponent(theme.title)}`}
                        alt={theme.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      /></a>
                      ) : (
                          <Link to={`/theme/${theme.slug}`}>
                            <img
                        src={theme.thumbnail || `https://placehold.co/600x400/dee3f8/303b5c?text=${encodeURIComponent(theme.title)}`}
                        alt={theme.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                          </Link>
                          )}

                      {theme.featured && (
                        <div className="absolute top-3 right-3 px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-semibold rounded-full flex items-center space-x-1">
                          <Star className="w-3 h-3 fill-current" />
                          <span>Featured</span>
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          {theme.type === 'external' ? (
                          <a
                            href={theme.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block"
                          >
                            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-sky-500 transition-colors">
                            {theme.title}
                          </h3>
                          </a>
                        ) : (
                          <Link to={`/theme/${theme.slug}`}>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-sky-500 transition-colors">
                            {theme.title}
                          </h3>
                          </Link>
                        )}
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {theme.description}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 mb-4">
                        <Link to={`/templates?category=${theme.category}`}>
                        <span className="px-3 py-1 bg-sky-50 text-sky-600 text-xs rounded-full font-medium capitalize">
                          {theme.category}
                        </span>
                        </Link>
                        {theme.type === 'external' && (
                          <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium flex items-center space-x-1">
                            <ExternalLink className="w-3 h-3" />
                            <span>External</span>
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-sky-500">${theme.price}</span>
                        {theme.type === 'external' ? (
                          <a
                            href={theme.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block"
                          >
                            <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all">
                              View Theme
                              <ExternalLink className="ml-2 w-4 h-4" />
                            </Button>
                          </a>
                        ) : (
                          <Link to={`/theme/${theme.slug}`}>
                            <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:shadow-lg transition-all">
                              View Details
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No themes found. Try adjusting your search or filters.</p>
              </div>
            )}

            <AdSlot slot="multiplex1" className="my-2"/>
          </div>


          {/* Sidebar */}
          <div className="lg:col-span-1">
            <ThemeSidebar />
          </div>
          
        </div>
      </div>
    </>
  );
};

export default Templates;